import React, { createContext, useContext, ReactNode } from 'react';
import { Provider } from 'react-redux';
import { store } from '../store/store';
import { useAuth as useAuthHook } from '../hooks/useAuth';
import { UserInfos } from '../store/userDataStore';

interface AuthContextType {
  login: (username: string, password: string) => Promise<{mfaRequired: boolean; sessionData?: any; message?: string} | undefined>;
  loginWithCasTicket: (ticket: string) => Promise<{success: boolean; error?: string} | undefined>;
  getCasLoginUrl: () => Promise<string>;
  submitMfaCode: (username: string, password: string, mfaToken: string, sessionData: any) => Promise<{success: boolean; error?: string} | undefined>;
  logout: () => Promise<void>;
  getValidToken: () => Promise<string | null>;
  authState: ReturnType<typeof useAuthHook>;
  user: UserInfos | null;
  resetAuthState: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Hook personnalisé pour utiliser le contexte d'authentification
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Composant interne qui utilise Redux
const AuthContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const authHook = useAuthHook();

  const value: AuthContextType = {
    login: authHook.login,
    loginWithCasTicket: authHook.loginWithCasTicket,
    getCasLoginUrl: authHook.getCasLoginUrl,
    submitMfaCode: authHook.submitMfaCode,
    logout: authHook.logout,
    getValidToken: authHook.getValidToken,
    authState: authHook,
    user: authHook.user,
    resetAuthState: authHook.resetAuthState
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Provider principal qui combine Redux et Context
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <Provider store={store}>
      <AuthContextProvider>
        {children}
      </AuthContextProvider>
    </Provider>
  );
};